import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Zap, AlertCircle } from "lucide-react";

const EMPTY = { tld: "", price: 999, description: "", enabled: true };

export default function AdminTLDs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dnapi, setDnapi] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/tlds"); setItems(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/domains/status").then(({ data }) => setDnapi(data)).catch(() => setDnapi({ live: false }));
  }, []);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/tlds", { ...form, price: Number(form.price) });
      toast.success(`Added .${form.tld}`); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try { await api.put(`/admin/tlds/${t.tld}`, { enabled: !t.enabled }); load(); }
    catch { toast.error("Failed"); }
  };

  const updatePrice = async (t, price) => {
    try { await api.put(`/admin/tlds/${t.tld}`, { price: Number(price) }); }
    catch { toast.error("Failed"); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete .${t.tld}?`)) return;
    try { await api.delete(`/admin/tlds/${t.tld}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Domain TLDs</h2>
        <p className="text-slate-400 text-sm">{items.length} TLDs · {items.filter(t=>t.enabled).length} enabled · Controls domain search results</p>
      </div>

      <div className="card p-4 flex items-start gap-3" data-testid="dnapi-status-card">
        {dnapi?.live ? (
          <>
            <Zap size={18} className="text-emerald-300 mt-0.5"/>
            <div>
              <div className="text-white font-medium">DomainNameAPI: <span className="text-emerald-300">Live</span></div>
              <p className="text-xs text-slate-400">Domain search, registration and transfer requests are routed to the live provider.</p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle size={18} className="text-amber-300 mt-0.5"/>
            <div>
              <div className="text-white font-medium">DomainNameAPI: <span className="text-amber-300">Not configured</span></div>
              <p className="text-xs text-slate-400">
                Set <code className="text-slate-300">DOMAIN_API_RESELLER_ID</code> and <code className="text-slate-300">DOMAIN_API_KEY</code> in <code className="text-slate-300">backend/.env</code> (mode: <code>{`{ote|prod}`}</code>) and restart the backend to enable live availability, registration & transfers.
              </p>
            </div>
          </>
        )}
      </div>

      <form onSubmit={create} className="card p-5 flex flex-wrap items-end gap-3" data-testid="tld-create-form">
        <div className="flex-1 min-w-[120px]"><label className="label">TLD</label><input required value={form.tld} onChange={e=>setForm({...form,tld:e.target.value})} placeholder="com" className="input" data-testid="tld-input"/></div>
        <div className="flex-1 min-w-[120px]"><label className="label">Price (INR/yr)</label><input type="number" required value={form.price} onChange={e=>setForm({...form,price:e.target.value})} className="input"/></div>
        <div className="flex-[2] min-w-[200px]"><label className="label">Description</label><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Short description" className="input"/></div>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="tld-add-btn">{saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Add TLD</button>
      </form>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400"/></div> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr><th className="py-3 px-4 text-left">TLD</th><th className="text-left">Description</th><th className="text-right">Price (INR)</th><th className="text-center">Enabled</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.tld} className="border-b border-slate-800">
                  <td className="py-3 px-4 text-cyan-300 font-mono">.{t.tld}</td>
                  <td className="text-slate-300">{t.description || <span className="text-slate-500">—</span>}</td>
                  <td className="text-right">
                    <input
                      type="number"
                      defaultValue={t.price}
                      onBlur={e=>updatePrice(t, e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm w-24 text-right"
                      data-testid={`tld-price-${t.tld}`}
                    />
                  </td>
                  <td className="text-center">
                    <button onClick={()=>toggle(t)} className={`inline-flex items-center justify-center w-9 h-5 rounded-full transition-colors ${t.enabled ? "bg-blue-600" : "bg-slate-700"}`}>
                      <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${t.enabled ? "translate-x-2" : "-translate-x-2"}`}/>
                    </button>
                  </td>
                  <td className="text-right pr-4"><button onClick={()=>del(t)} className="text-slate-400 hover:text-rose-400"><Trash2 size={15}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
